import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProgressBar } from "react-bootstrap";
import api from "../../api";
import { LoadingState } from "../../components/ui/LoadingState";
import { ErrorState } from "../../components/ui/ErrorState";
import { formatDateOrDash } from "./projectWorkspaceUtils";
import { AuthContext } from "../../context/AuthContext";

// Overview tab -- renders the fields already fetched at the page level
// (GET /projects/:id/overview, see ProjectWorkspacePage.jsx) plus a
// per-phase progress summary sourced from GET /projects/:id/process (its
// own lazy fetch, since /overview does not include the full phase list).
export default function ProjectWorkspaceOverviewTab({ projectId, overview }) {
  const navigate = useNavigate();
  // Role-aware base path -- see ProjectWorkspaceHeader.jsx's own comment;
  // this tab is reached through the same shared workspace component.
  const { user } = useContext(AuthContext) || {};
  const basePath = `/${user?.role || "admin"}`;
  const [phases, setPhases] = useState([]);
  const [loadingPhases, setLoadingPhases] = useState(true);
  const [phaseError, setPhaseError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoadingPhases(true);
    setPhaseError("");
    api
      .get(`/projects/${projectId}/process`)
      .then((res) => {
        if (cancelled) return;
        setPhases(Array.isArray(res.data?.data?.phases) ? res.data.data.phases : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setPhaseError(err.response?.data?.message || "Failed to load phase progress");
      })
      .finally(() => {
        if (!cancelled) setLoadingPhases(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (!overview) return null;

  const { project, ticketBreakdown, ticketCounts, upcomingMilestone, processConfigured } = overview;

  return (
    <div className="pw-overview-grid">
      <div>
        <div className="pw-card">
          <h6>Description</h6>
          <p className="mb-0" style={{ fontSize: 13, color: "#6b6259" }}>
            {project?.description || <span className="text-muted">No description provided.</span>}
          </p>
        </div>

        <div className="pw-card">
          <h6>Ticket Breakdown</h6>
          <div className="pw-stat-row">
            <div className="pw-stat-tile"><strong>{ticketBreakdown?.total ?? 0}</strong><span>Total</span></div>
            <div className="pw-stat-tile"><strong>{ticketBreakdown?.completed ?? 0}</strong><span>Completed</span></div>
            <div className="pw-stat-tile"><strong>{ticketBreakdown?.cancelled ?? 0}</strong><span>Cancelled</span></div>
            <div className="pw-stat-tile"><strong>{ticketBreakdown?.overdue ?? 0}</strong><span>Overdue</span></div>
          </div>

          {Array.isArray(ticketBreakdown?.byStatus) && ticketBreakdown.byStatus.length > 0 && (
            <div className="mt-3 d-flex flex-wrap gap-2">
              {ticketBreakdown.byStatus.map((s) => (
                <span
                  key={s.status_id ?? s.status_name}
                  className="pw-chip pw-chip-secondary"
                  style={s.status_color ? { background: `${s.status_color}22`, color: s.status_color } : undefined}
                >
                  {s.status_name}: {s.count}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="pw-card">
          <h6>Phase Progress</h6>
          {loadingPhases ? (
            <LoadingState message="Loading phase progress…" />
          ) : phaseError ? (
            <ErrorState message={phaseError} />
          ) : !processConfigured ? (
            <p className="text-muted small mb-0">Project process not configured yet — see the Process tab.</p>
          ) : phases.length === 0 ? (
            <p className="text-muted small mb-0">No phases configured.</p>
          ) : (
            <div className="pw-phase-progress-list">
              {phases.map((p) => (
                <div className="pw-phase-progress-item" key={p.id}>
                  <span className="pw-phase-name">{p.name}</span>
                  {p.progress === null || p.progress === undefined ? (
                    <span className="text-muted small flex-fill">No tasks added</span>
                  ) : (
                    <>
                      <ProgressBar now={p.progress} />
                      <span className="pw-phase-pct">{Math.round(p.progress)}%</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="pw-card">
          <h6>At a Glance</h6>
          <div className="d-flex flex-column gap-2" style={{ fontSize: 13 }}>
            <div className="d-flex justify-content-between"><span className="text-muted">Eligible tickets</span><strong>{ticketCounts?.eligible ?? 0}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Completed tickets</span><strong>{ticketCounts?.completed ?? 0}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Start date</span><strong>{formatDateOrDash(project?.start_date)}</strong></div>
            <div className="d-flex justify-content-between"><span className="text-muted">Target end</span><strong>{formatDateOrDash(project?.end_date)}</strong></div>
          </div>
        </div>

        <div className="pw-card">
          <h6>Upcoming Milestone</h6>
          {upcomingMilestone ? (
            <div>
              <strong style={{ fontSize: 14 }}>{upcomingMilestone.name}</strong>
              <div className="text-muted small mt-1">
                Planned: {formatDateOrDash(upcomingMilestone.planned_date)}
              </div>
            </div>
          ) : (
            <p className="text-muted small mb-0">No upcoming milestone.</p>
          )}
        </div>

        <div className="pw-card">
          <h6>Project Setup</h6>
          <p className="text-muted small">
            Members and modules are managed from the existing Project Management workspace.
          </p>
          <button
            type="button"
            className="unit-btn unit-btn-light w-100"
            onClick={() => navigate(`${basePath}/projects?project=${projectId}&tab=modules`)}
          >
            <i className="fa-solid fa-cubes" aria-hidden="true"></i> Manage Modules
          </button>
        </div>
      </div>
    </div>
  );
}
